import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowRight, User, Lock, LineChart, TrendingDown, TrendingUp, Receipt,FileText } from 'lucide-react';
import AuthLayout from '../../../layouts/AuthLayout';
import FormInput from '../../../components/forms/FormInput';
import SubmitButton from '../../../components/buttons/SubmitButton';
import Alert from '../../../components/feedback/Alert';
import { APP_NAME } from '../../../constants/appConstants';
import loginBackgroundImage from '../../../assets/calculate-money.jpg'

const LoginPage = () => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login: authLogin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await authLogin(login, password, 'user');
      // If we reach here, login was successful and navigation happened
    } catch (err) {
      // Display the actual error message from backend
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const leftPanel = (
    <>
      <h2 className="text-xl font-bold mb-2">Welcome Back</h2>
      <p className="mb-6 text-blue-100 text-xs">
        Manage your business finances — track expenses, monitor revenue, handle bills, and generate proformas with ease.
      </p>

      <div className="space-y-5">

        {/* Expenses */}
        <div className="flex items-start">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0">
            <TrendingDown className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-0.5">Manage Expenses</h3>
            <p className="text-blue-100 text-xs">
              Record and track your business spending.
            </p>
          </div>
        </div>

        {/* Revenue */}
        <div className="flex items-start">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0">
            <TrendingUp className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-0.5">Monitor Revenue</h3>
            <p className="text-blue-100 text-xs">
              Keep track of all income streams.
            </p>
          </div>
        </div>

        {/* Bills */}
        <div className="flex items-start">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0">
            <Receipt className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-0.5">Manage Bills</h3>
            <p className="text-blue-100 text-xs">
              View, pay, and organize all bills.
            </p>
          </div>
        </div>

        {/* Proformas */}
        <div className="flex items-start">
          <div className="h-9 w-9 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0">
            <FileText className="h-4 w-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-sm mb-0.5">Generate Proformas</h3>
            <p className="text-blue-100 text-xs">
              Create and manage proforma invoices easily.
            </p>
          </div>
        </div>

      </div>
    </>
  );


  return (
    <AuthLayout 
      leftPanelContent={leftPanel} 
      isLoginPage={true}
      backgroundImage={loginBackgroundImage}
    >
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sign in to your account</h1>
          <p className="text-sm text-gray-600">Enter your credentials to continue</p>
        </div>
        
        <Alert message={error} type="error" />

        <form className="space-y-5 mt-6" onSubmit={handleSubmit}>
          <FormInput
            id="login"
            name="login"
            label="Username or Email"
            type="text"
            required
            icon={User}
            placeholder="Enter your username or email"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="username"
          />

          <FormInput
            id="password"
            name="password"
            label="Password"
            type="password"
            required
            icon={Lock}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />

          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-primary-600 hover:text-primary-700">
                Forgot password?
              </Link>
            </div>
          </div>

          <div className="pt-2">
            <SubmitButton 
              isLoading={loading} 
              icon={ArrowRight}
              fullWidth={true}
            >
              Sign in
            </SubmitButton>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700">
              Register now
            </Link>
          </p>
          <p className="text-xs text-gray-500 mt-2">
            <Link to="/superadmin/login" className="text-gray-400 hover:text-gray-600">
              System Administrator Access
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;